import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 900000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — handle 401, refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      const refresh = localStorage.getItem('refresh_token');
      if (refresh) {
        try {
          const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: refresh });
          localStorage.setItem('access_token', data.access_token);
          original.headers.Authorization = `Bearer ${data.access_token}`;
          return api(original);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      } else {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth ───────────────────────────────────────────────
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: (token) => api.post('/auth/refresh', { refresh_token: token }),
  google: (credential) => api.post('/auth/google', { credential }),
  register: (payload) => api.post('/auth/register', payload),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};


export const platformAPI = {
  get: () => api.get('/admin/platform-settings'),
  update: (payload) => api.put('/admin/platform-settings', payload),
};

export const assistantAPI = {
  chat: (message, history, current_page, image) => api.post('/assistant/chat', { message, history, current_page, image }),
};

// ─── Posts / Wizard ─────────────────────────────────────
export const postsAPI = {
  upload: (formData, onProgress) =>
    api.post('/posts/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / e.total)),
    }),
  analyze: (mediaId, projectId) => api.post('/posts/analyze', { media_id: mediaId, project_id: projectId }),
  generate: (payload) => api.post('/posts/generate', payload),
  regenerate: (payload) => api.post('/posts/regenerate', payload),
  publish: (payload) => api.post('/posts/publish', payload),
  scheduleStory: (payload) => api.post('/posts/schedule-story', payload),
  list: (params) => api.get('/posts', { params }),
  byId: (id) => api.get(`/posts/${id}`),
  stats: (id) => api.get(`/posts/${id}/stats`),
  delete: (id) => api.delete(`/posts/${id}`),
  reschedule: (id, scheduledAt) => api.patch(`/posts/${id}/reschedule`, { scheduled_at: scheduledAt }),
};

// ─── Analytics ──────────────────────────────────────────
export const appointmentsAPI = {
  list: () => api.get('/crm/appointments'),
  listDeleted: () => api.get('/crm/appointments/deleted'),
  create: (payload) => api.post('/crm/appointments', payload),
  update: (id, payload) => api.patch(`/crm/appointments/${id}`, payload),
  delete: (id) => api.delete(`/crm/appointments/${id}`),
};

export const analyticsAPI = {
  dashboard: (params) => api.get('/analytics/dashboard', { params }),
  postPerformance: (postId) => api.get(`/analytics/posts/${postId}`),
  networkSummary: (network, params) => api.get(`/analytics/networks/${network}`, { params }),
  syncStats: () => api.post('/analytics/sync'),
  topPosts: (params) => api.get('/analytics/top-posts', { params }),
};

// ─── Projects ───────────────────────────────────────────
export const projectsAPI = {
  list: () => api.get('/projects'),
  byId: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  posts: (id) => api.get(`/projects/${id}/posts`),
};

// ─── Social Accounts ────────────────────────────────────
export const accountsAPI = {
  list: () => api.get('/accounts'),
  connect: (network, data) => api.post(`/accounts/${network}/connect`, data),
  disconnect: (id) => api.delete(`/accounts/${id}`),
  pages: (id) => api.get(`/accounts/${id}/pages`),
  refresh: (id) => api.post(`/accounts/${id}/refresh`),
};

// ─── AI Providers ───────────────────────────────────────
export const providersAPI = {
  list: () => api.get('/providers'),
  byId: (id) => api.get(`/providers/${id}`),
  create: (data) => api.post('/providers', data),
  update: (id, data) => api.put(`/providers/${id}`, data),
  delete: (id) => api.delete(`/providers/${id}`),
  setActive: (id) => api.post(`/providers/${id}/activate`),
  activateImage: (id) => api.post(`/providers/${id}/activate-image`),
  test: (id) => api.post(`/providers/${id}/test`),
};

// ─── Prompt Templates ───────────────────────────────────
export const productsAPI = {
  list: () => api.get('/crm/products'),
};

export const automationAPI = {
  listRules: () => api.get('/automation/rules'),
  createRule: (data) => api.post('/automation/rules', data),
  updateRule: (id, data) => api.put(`/automation/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/automation/rules/${id}`),
  pendingReview: () => api.get('/automation/pending-review'),
  approve: (id, payload) => api.post(`/automation/pending-review/${id}/approve`, payload),
  reject: (id) => api.post(`/automation/pending-review/${id}/reject`),
};

export const templatesAPI = {
  list: (params) => api.get('/templates', { params }),
  byId: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

// ─── Users ──────────────────────────────────────────────
export const usersAPI = {
  list: () => api.get('/users'),
  byId: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  updatePassword: (id, data) => api.put(`/users/${id}/password`, data),
};

// ─── Notifications ──────────────────────────────────────
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  subscribe: (subscription) => api.post('/notifications/subscribe', subscription),
};

export default api;

export const mediaAPI = {
  list:   ()   => api.get('/media'),
  delete: (id) => api.delete(`/media/${id}`),
};

export const activityAPI = {
  list: (limit = 50) => api.get(`/activity?limit=${limit}`),
};

export const hashtagsAPI = {
  history: () => api.get('/hashtags/history'),
};

export const analyticsExtraAPI = {
  bestHours:  () => api.get('/analytics/best-hours'),
  exportCsv:  () => api.get('/analytics/export-csv', { responseType: 'blob' }),
};

export const postsRetryAPI = {
  retry: (id) => api.post(`/posts/${id}/retry`),
};

export const captionTemplatesAPI = {
  list:   (network) => api.get(`/caption-templates${network ? `?network=${network}` : ''}`),
  create: (data)    => api.post('/caption-templates', data),
  delete: (id)      => api.delete(`/caption-templates/${id}`),
};

export const bulkPublishAPI = {
  publish: (data) => api.post('/posts/bulk-publish', data),
};

export const adsAPI = {
  // Cuentas publicitarias
  listAccounts:       ()              => api.get('/ads/accounts'),
  syncAccounts:       (network)       => api.post(`/ads/accounts/sync?network=${network}`),

  // Campañas
  listCampaigns:      (network)       => api.get(`/ads/campaigns${network ? `?network=${network}` : ''}`),
  createCampaign:     (data)          => api.post('/ads/campaigns', data),
  updateStatus:       (id, status)    => api.patch(`/ads/campaigns/${id}/status?status=${status}`),
  updateBudget:       (id, budget)    => api.patch(`/ads/campaigns/${id}/budget?budget=${budget}`),
  deleteCampaign:     (id)            => api.delete(`/ads/campaigns/${id}`),
  getInsights:        (id, preset)    => api.get(`/ads/campaigns/${id}/insights?date_preset=${preset || 'last_30d'}`),
  syncFromPlatform:   (accountId)     => api.post(`/ads/campaigns/sync-from-platform?ad_account_id=${accountId}`),
  overview:           ()              => api.get('/ads/overview'),

  // Ad Sets
  listAdSets:         (campaignId)    => api.get(`/ads/campaigns/${campaignId}/adsets`),
  createAdSet:        (campaignId, data) => api.post('/ads/adsets', { ...data, campaign_id: campaignId }),

  // Ads
  listAds:            (adSetId)       => api.get(`/ads/adsets/${adSetId}/ads`),
  createAd:           (data)          => api.post('/ads/ads', data),

  // Targeting
  searchInterests:    (q)             => api.get(`/ads/targeting/interests?q=${encodeURIComponent(q)}`),
  updateCampaign:     (id, data)      => api.patch(`/ads/campaigns/${id}`, data),
  updateAdSetStatus:  (id, status)    => api.patch(`/ads/adsets/${id}/status?status=${status}`),
  deleteAdSet:        (id)            => api.delete(`/ads/adsets/${id}`),
  updateAdStatus:     (id, status)    => api.patch(`/ads/ads/${id}/status?status=${status}`),
  deleteAd:           (id)            => api.delete(`/ads/ads/${id}`),
  updateAd:           (id, data)      => api.patch(`/ads/ads/${id}`, data),
  updateAdSet:        (id, data)      => api.patch(`/ads/adsets/${id}`, data),
  getAdSetInsights:   (id, preset)    => api.get(`/ads/adsets/${id}/insights?date_preset=${preset||"last_30d"}`),
  getAdInsights:      (id, preset)    => api.get(`/ads/ads/${id}/insights?date_preset=${preset||"last_30d"}`),
  getAdPreviews:      (id)            => api.get(`/ads/ads/${id}/previews`),
  uploadAdVideo:      (adAccountId, file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post(`/ads/videos/upload?ad_account_id=${adAccountId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  checkVideoStatus:   (videoId)       => api.get(`/ads/videos/${videoId}/status`),
};

export const billingAPI = {
  // Planes
  listPlans:          ()                    => api.get('/billing/plans'),
  createPlan:         (data)                => api.post('/billing/plans', data),
  updatePlan:         (id, data)            => api.put(`/billing/plans/${id}`, data),
  deletePlan:         (id)                  => api.delete(`/billing/plans/${id}`),

  // Suscripción del usuario
  getSubscription:    ()                    => api.get('/billing/subscription'),
  effectivePlan:      ()                    => api.get('/billing/effective-plan'),
  individualPlan:     ()                    => api.get('/billing/individual-plan'),
  cancelSubscription: ()                    => api.post('/billing/subscription/cancel'),

  // Checkout
  checkout:           (data)                => api.post('/billing/checkout', data),

  // Pagos
  listPayments:       ()                    => api.get('/billing/payments'),

  // Pasarelas (admin)
  listGateways:       ()                    => api.get('/billing/gateways'),
  upsertGateway:      (name, data)          => api.put(`/billing/gateways/${name}`, data),

  // Admin — consumo
  adminOverview:      ()                    => api.get('/billing/admin/overview'),
  adminUsers:         ()                    => api.get('/billing/admin/users-consumption'),
  adminProviderCosts: ()                    => api.get('/billing/admin/provider-costs'),
  adminTokenUsage:    (userId, provider)    => api.get(`/billing/admin/token-usage${userId ? `?user_id=${userId}` : ''}${provider ? `&provider=${provider}` : ''}`),
  adminActivateUser:  (userId, planId, cycle) => api.post(`/billing/admin/activate-user?user_id=${userId}&plan_id=${planId}&billing_cycle=${cycle}`),
};
